<template>
  <div class="sms-guidance" v-if="hasText || guidance.severity !== 'ok'">
    <!-- Live credit counter (always, subtle when fine) -->
    <div v-if="showCounter" class="sms-guidance__counter" :class="'is-' + guidance.severity">
      <span class="sms-guidance__dot"></span>
      <span v-if="guidance.messageType === 'mms'">Picture message · {{ guidance.creditsLabel }} to send</span>
      <span v-else>{{ guidance.isEstimate ? '~' : '' }}{{ guidance.charsLabel }} · {{ guidance.isEstimate ? '~' : '' }}{{ guidance.creditsLabel }} to send</span>
      <span v-if="balanceLabel" class="sms-guidance__balance" data-test="sms-guidance-balance">· {{ balanceLabel }}</span>
      <!-- Info tooltip: what a credit is, why emojis/special characters cost more, and — when the
           message has {{variables}} — that the count is an estimate. One shared explanation
           (utils/smsGuidance), so the wording is identical on every SMS surface. -->
      <i
        class="fa fa-info-circle sms-guidance__info"
        tabindex="0"
        v-b-tooltip.hover.top="guidance.explainer"
        :aria-label="guidance.explainer"
      ></i>
    </div>

    <!-- Warn / block banner with standardized tips -->
    <b-alert
      v-if="guidance.severity !== 'ok'"
      :variant="guidance.severity === 'block' ? 'danger' : 'warning'"
      show
      class="sms-guidance__banner mt-1 mb-0"
    >
      <div class="sms-guidance__title">
        <i :class="guidance.severity === 'block' ? 'fa fa-ban' : 'fa fa-exclamation-triangle'" class="mr-1"></i>
        {{ guidance.title }}
      </div>
      <div class="sms-guidance__message">{{ guidance.message }}</div>
      <ul v-if="guidance.tips.length" class="sms-guidance__tips">
        <li v-for="(tip, i) in guidance.tips" :key="i">{{ tip }}</li>
      </ul>
    </b-alert>
  </div>
</template>

<script>
/**
 * SmsGuidance — the ONE reusable SMS composer indicator. Drop it under any SMS text
 * input; it shows the live credit count and, when a message is long / has cost-
 * inflating special characters / is over the carrier limit, a standardized
 * warn-or-block banner with tips. All copy + thresholds come from utils/smsGuidance
 * so every SMS surface in the app behaves identically.
 *
 * INFORMATION ONLY. Cost is shown here — credits this message will use + the site's
 * remaining balance — and never asked about: no send surface may put a "this costs N
 * credits — send it?" prompt in front of the user (removed 2026-09-27 after user
 * complaints; guarded in cypress/node-tests/sms-cost-no-prompt.test.js). The one thing that
 * still stops a send is a hard block (over the carrier limit — it can't be delivered).
 *
 * Usage:
 *   <SmsGuidance :text="smsText" @change="g => smsBlocked = g.blocked" />
 *   ...then bind the Send button:  :disabled="smsBlocked"
 */
import { getSmsGuidance } from '../utils/smsGuidance';

export default {
  name: 'SmsGuidance',
  props: {
    text: { type: String, default: '' },
    // Pass a non-empty array when the message carries media (picture message / MMS).
    media: { type: Array, default: null },
    // Campaigns let the customer keep as-typed formatting; other surfaces omit this.
    smsFormatting: { type: String, default: undefined },
    // Tight composers can hide the inline counter and keep only the banner.
    showCounter: { type: Boolean, default: true },
    // TEMPLATE surfaces (SMS templates, template pickers, template-backed composers)
    // carry unresolved {{merge_tags}}. Set this so guidance measures the RENDERED text
    // with conservative sample values instead of the literal template — matching what
    // the backend actually sends/bills. See utils/smsGuidance getSmsGuidance().
    substituteTags: { type: Boolean, default: false },
    // Real merge values when known (e.g. a specific patient) — measures the exact
    // send size, catching accented-name UCS-2 cost traps the sample values can't.
    vars: { type: Object, default: null },
    // Show the site's remaining SMS credits next to the cost. Template AUTHORING screens
    // (wiki editor, review settings) turn this off — nothing is being sent there.
    showBalance: { type: Boolean, default: true },
  },
  computed: {
    hasText() {
      return !!(this.text && this.text.trim());
    },
    // "1,240 credits left" from the same live wallet the header pill reads. Hidden until the
    // balance has loaded (never show a misleading 0) and on surfaces that opt out.
    balanceLabel() {
      const wallet = this.showBalance && this.$store && this.$store.state && this.$store.state.wallet;
      if (!wallet || !wallet.hasBalance) return '';
      const n = Number(wallet.creditsAvailable) || 0;
      return `${n.toLocaleString()} credit${n === 1 ? '' : 's'} left`;
    },
    guidance() {
      return getSmsGuidance(this.text || '', {
        media: this.media,
        smsFormatting: this.smsFormatting,
        substituteTags: this.substituteTags,
        vars: this.vars,
      });
    },
  },
  watch: {
    guidance: {
      immediate: true,
      handler(g) {
        // Let the parent disable Send on a block, or react to the credit count.
        this.$emit('change', g);
      },
    },
  },
};
</script>

<style scoped>
.sms-guidance {
  font-size: 0.8125rem;
}
.sms-guidance__counter {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #798892;
  font-weight: 600;
}
.sms-guidance__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #a3aeb7;
  flex: none;
}
.sms-guidance__counter.is-warn { color: #a8620d; }
.sms-guidance__counter.is-warn .sms-guidance__dot { background: #f0801a; }
.sms-guidance__counter.is-block { color: #c0392b; }
.sms-guidance__counter.is-block .sms-guidance__dot { background: #f55d5d; }

.sms-guidance__balance {
  color: #798892;
  font-weight: 400;
}

.sms-guidance__info {
  color: #9aa7b1;
  cursor: help;
  font-weight: 400;
}
.sms-guidance__info:hover,
.sms-guidance__info:focus {
  color: #667085;
  outline: none;
}

.sms-guidance__banner {
  font-size: 0.8125rem;
  padding: 0.6rem 0.8rem;
}
.sms-guidance__title {
  font-weight: 700;
}
.sms-guidance__message {
  margin-top: 0.15rem;
}
.sms-guidance__tips {
  margin: 0.4rem 0 0;
  padding-left: 1.1rem;
}
.sms-guidance__tips li {
  margin-bottom: 0.2rem;
}

/* Block banner: white text on a deep red. The global .alert-danger uses DARK text on a lighter
   red for contrast (white on that red is only ~3.2:1); we want white here, so we deepen the red
   to #c0392b where white clears WCAG AA (~5.4:1). Scoped to this component — the global danger
   alert and its pinned contrast test stay untouched. */
.sms-guidance ::v-deep .sms-guidance__banner.alert-danger {
  background: #c0392b;
  border-color: #a93226;
  color: #fff;
}
</style>
